import React, { useCallback, useEffect, useState } from 'react';
import { useObserver } from 'mobx-react-lite';
import styled from 'styled-components';
import tagsStore from '../../../stores/tags.store';
import uptimeStore from '../../../stores/uptime.store';
import LoaderComponent from '../../components/Loader.component';
import Header from '../../components/header/Header';
import {
    DeleteServerZoneStyled,
    EditZoneStyled,
    StyledEditWrapper,
    TagsWrapperStyled
} from './ServerEdit.page';
import { GoBackStyled } from '../../../style/components/common.styled';
import serverStore from '../../../stores/server.store';
import TagsList from '../../components/tags/TagsList';
import { EuiButton, EuiIcon } from '@elastic/eui';
import TagsModal from '../../components/tags/TagsModal';
import EditUptimeData from './components/serverEdit/EditUptimeData';
import { Redirect, useHistory } from 'react-router-dom';
import { addToast } from '../../components/toasts/Toast';
import { MONITOR_TYPES } from '../../../constants/monitor.types';
import mongoDbStore from "../../../stores/mongodb.store";
import utilsService from '../../../servises/utils.service';
import { loadDetailData } from '../../../servises/detail-loading.service';
import MonitorLoadError from './components/MonitorLoadError';

const EditSurfaceStyled = styled.div`
  width: 100%;
  min-height: 200px;
  padding: 18px;
  border-radius: 8px;
  background-color: rgb(29,30,36);
  box-shadow: rgb(0 0 0 / 28%) 0px 0.9px 4px, rgb(0 0 0 / 21%) 0px 2.6px 8px, rgb(0 0 0 / 18%) 0px 5.7px 12px, rgb(0 0 0 / 14%) 0px 15px 15px;

  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const EditHeroStyled = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-gap: 20px;
  align-items: start;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }

  .eyebrow {
    color: #36A2EF;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 7px;
    text-transform: uppercase;
  }

  .summary {
    color: rgba(255, 255, 255, 0.62);
    font-size: 13px;
    line-height: 1.55;
    margin-top: 8px;
    max-width: 680px;
  }

  .hero-actions {
    display: flex;
    justify-content: flex-end;

    @media (max-width: 760px) {
      justify-content: flex-start;
    }
  }
`;

const MonitorMetaGridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  grid-gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const MonitorMetaItemStyled = styled.div`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 6px;
  min-width: 0;
  padding: 10px 12px;

  .label {
    color: rgba(255, 255, 255, 0.48);
    display: block;
    font-size: 11px;
    margin-bottom: 5px;
  }

  .value {
    color: rgba(255, 255, 255, 0.9);
    display: block;
    font-family: Consolas, Monaco, monospace;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ManagementGridStyled = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.8fr);
  grid-gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ColumnStyled = styled.div`
  display: grid;
  grid-gap: 16px;
  align-content: start;
`;

const SectionHeaderStyled = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;

  .section-icon {
    align-items: center;
    background: rgba(0, 128, 213, 0.12);
    border: 1px solid rgba(0, 128, 213, 0.25);
    border-radius: 6px;
    color: #36A2EF;
    display: flex;
    flex: 0 0 30px;
    height: 30px;
    justify-content: center;
    width: 30px;
  }

  p {
    color: rgba(255, 255, 255, 0.58);
    font-size: 12px;
    line-height: 1.45;
    margin: 5px 0 0;
  }
`;

const TagsActionsStyled = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const DangerActionsStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const UptimeEditPage = props => {
    document.title = 'Uptime Edit - SyAgent';
    const { uptimeId } = props.match.params;

    const redirect = () => {
        props.history.push('/servers');
    };

    return useObserver(() => (
        <Observed
            tags={ tagsStore.tags }
            uptimeId={ uptimeId }
            handleRedirect={ redirect }
            items={ uptimeStore.uptimeListObj }
        />
    ));
};

export default UptimeEditPage;

const Observed = props => {
    const history = useHistory();

    const { uptimeId, handleRedirect, items } = props;

    const [loader, setLoader] = useState(true);
    const [loadedUptimeId, setLoadedUptimeId] = useState(null);
    const [uptimeListLoaded, setUptimeListLoaded] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadSequence, setLoadSequence] = useState(0);
    const [areYouSureState, setAreYouSureState] = useState(false);
    const [deleteSending, setDeleteSending] = useState(false);
    const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);

    const uptime = items[uptimeId];
    const intervalText = uptime ? utilsService.calculateEveryTime(uptime) : 'No schedule';

    const handleDelete = useCallback(async e => {
        if (deleteSending) {
            return;
        }

        setDeleteSending(true);
        try {
            await uptimeStore.deleteMonitor(uptimeId);
            addToast('Monitor deleted', 'suc');
            handleRedirect();
        } catch (error) {
            addToast(error.message || 'Failed to delete monitor', 'error');
        } finally {
            setDeleteSending(false);
        }

    }, [deleteSending, handleRedirect, uptimeId]);

    const goSomewhere = e => {
        e.preventDefault();
        history.push(`/uptime/${ uptimeId }`);
    };

    useEffect(() => {
        let active = true;
        setLoader(true);
        setUptimeListLoaded(false);
        setLoadFailed(false);
        const getsData = async () => {
            const secondaryFailed = await loadDetailData(
                uptimeStore.getUptime(true),
                [
                    serverStore.getServers(),
                    tagsStore.getTags(),
                    mongoDbStore.getMongoDb()
                ]
            );
            if (secondaryFailed) {
                addToast('Some monitor settings could not be loaded', 'error');
            }
            if (active) {
                setUptimeListLoaded(true);
            }
        };

        getsData()
            .catch(error => {
                addToast(error.message || 'Failed to load monitor', 'error');
                if (active) {
                    setLoadFailed(true);
                }
            })
            .finally(() => {
                if (active) {
                    setLoadedUptimeId(uptimeId);
                    setLoader(false);
                }
            });
        return () => {
            active = false;
        };
    }, [uptimeId, loadSequence]);

    return (
        <main className="servers">
            { (loader || loadedUptimeId !== uptimeId) && (
                <LoaderComponent/>
            ) }
            { !loader && loadedUptimeId === uptimeId && uptimeListLoaded && !uptime && (
                <Redirect to="/404"/>
            ) }
            { !loader && loadedUptimeId === uptimeId && loadFailed && (
                <MonitorLoadError onRetry={ () => setLoadSequence(value => value + 1) }/>
            ) }
            { !loader && loadedUptimeId === uptimeId && !loadFailed && uptimeListLoaded && uptime && (
                <>
                    <Header/>
                    <StyledEditWrapper>
                        <GoBackStyled>
                            <EuiButton onClick={ goSomewhere }
                                       size="s"
                                       iconType={ 'returnKey' }
                                       color={ 'text' }>
                                Go back to Uptime
                            </EuiButton>
                        </GoBackStyled>
                        <EditSurfaceStyled>
                            <EditHeroStyled>
                                <div>
                                    <div className="eyebrow">Uptime monitor settings</div>
                                    <h1>{ uptime ? uptime.name : 'Monitor management' }</h1>
                                    <div className="summary">
                                        Tune the endpoint, schedule, tags, and notification rules for this uptime check.
                                    </div>
                                </div>
                                <div className="hero-actions">
                                    <EuiButton
                                        onClick={ goSomewhere }
                                        size="s"
                                        iconType={ 'uptimeApp' }
                                        color={ 'primary' }
                                        fill
                                    >
                                        View monitor
                                    </EuiButton>
                                </div>
                            </EditHeroStyled>

                            { uptime && (
                                <MonitorMetaGridStyled>
                                    <MonitorMetaItemStyled>
                                        <span className="label">Monitor ID</span>
                                        <span className="value" title={ uptime._id }>{ uptime._id }</span>
                                    </MonitorMetaItemStyled>
                                    <MonitorMetaItemStyled>
                                        <span className="label">Type</span>
                                        <span className="value">{ uptime.type || 'Unknown' }</span>
                                    </MonitorMetaItemStyled>
                                    <MonitorMetaItemStyled>
                                        <span className="label">Schedule</span>
                                        <span className="value">{ intervalText || 'No schedule' }</span>
                                    </MonitorMetaItemStyled>
                                    <MonitorMetaItemStyled>
                                        <span className="label">Status</span>
                                        <span className="value">{ uptime.isEnabled ? 'Enabled' : 'Paused' }</span>
                                    </MonitorMetaItemStyled>
                                </MonitorMetaGridStyled>
                            ) }

                            <ManagementGridStyled>
                                <ColumnStyled>
                                    <EditZoneStyled className="bonya" accentColor="rgb(0, 128, 213)">
                                        <SectionHeaderStyled>
                                            <span className="section-icon">
                                                <EuiIcon type="uptimeApp" size="m"/>
                                            </span>
                                            <div>
                                                <h3>Monitor settings</h3>
                                                <p>Control the target, schedule, response rules, and request details used by this check.</p>
                                            </div>
                                        </SectionHeaderStyled>
                                        { uptime && (
                                            <EditUptimeData
                                                uptime={ uptime }
                                            />
                                        ) }
                                    </EditZoneStyled>
                                </ColumnStyled>

                                <ColumnStyled>
                                    <EditZoneStyled className="bonya" accentColor="#50bc5e">
                                        <SectionHeaderStyled>
                                            <span className="section-icon">
                                                <EuiIcon type="tag" size="m"/>
                                            </span>
                                            <div>
                                                <h3>Tags</h3>
                                                <p>Group this uptime monitor by service, environment, customer, or ownership.</p>
                                            </div>
                                        </SectionHeaderStyled>
                                        <TagsWrapperStyled>
                                            { uptime && (
                                                <TagsList
                                                    serverId={ uptimeId }
                                                    monitorType={ MONITOR_TYPES.HEARTBEAT }
                                                    serverTags={ uptime.tags }
                                                />
                                            ) }
                                        </TagsWrapperStyled>
                                        <TagsActionsStyled>
                                            <EuiButton
                                                size="s"
                                                onClick={ () => setIsTagsModalOpen(true) }
                                                iconType="plusInCircle"
                                                fill
                                            >
                                                Edit tags
                                            </EuiButton>
                                        </TagsActionsStyled>
                                    </EditZoneStyled>

                                    <DeleteServerZoneStyled className="bonya">
                                        <SectionHeaderStyled>
                                            <span className="section-icon">
                                                <EuiIcon type="trash" size="m"/>
                                            </span>
                                            <div>
                                                <h3>Danger zone</h3>
                                                <p>Deleting this monitor removes its schedule and sends you back to the monitor list.</p>
                                            </div>
                                        </SectionHeaderStyled>
                                        { !areYouSureState && (
                                            <EuiButton
                                                size="s"
                                                iconType={ 'trash' }
                                                color="danger"

                                                onClick={ () => setAreYouSureState(
                                                    true) }
                                                fill
                                            >Delete monitor
                                            </EuiButton>
                                        ) }

                                        { areYouSureState && (
                                            <>
                                                <div
                                                    className="are-you-sure">Delete { uptime ? uptime.name : 'this monitor' }?
                                                </div>
                                                <DangerActionsStyled>
                                                    <EuiButton
                                                        size="s"
                                                        color="danger"

                                                        onClick={ handleDelete }
                                                        isLoading={ deleteSending }
                                                        fill
                                                    >
                                                        Yes, delete
                                                    </EuiButton>

                                                    <EuiButton
                                                        size="s"
                                                        onClick={ () => setAreYouSureState(
                                                            false) }
                                                    >
                                                        Cancel
                                                    </EuiButton>
                                                </DangerActionsStyled>
                                            </>
                                        ) }
                                    </DeleteServerZoneStyled>
                                </ColumnStyled>
                            </ManagementGridStyled>
                        </EditSurfaceStyled>
                    </StyledEditWrapper>
                </>
            ) }
            { isTagsModalOpen && (
                <TagsModal close={ () => setIsTagsModalOpen(false) }/>
            ) }
        </main>
    );
};
